import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FaCheckCircle, FaMapMarkerAlt, FaMinus, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Link, useHistory } from 'react-router-dom';
import { BusinessStatusContext } from '../contexts/BusinessStatusContext';
import { BasketContext } from '../contexts/BasketContext';
import { AuthContext } from '../contexts/AuthContext';
import { buildApiUrl, buildAuthHeaders, GOOGLE_MAPS_API_KEY } from '../config/api';

const initialForm = { name: '', phone: '', addressSearch: '', streetNumber: '', streetName: '', suburb: '', postalCode: '', latitude: '', longitude: '', deliveryNote: '', deliveryTime: 'ASAP' };
const money = value => `R${Number(value || 0).toFixed(2)}`;
let googleMapsLoader;

function loadGoogleMaps(key) {
    if (!key) return Promise.reject(new Error('Address search is not configured.'));
    if (window.google?.maps?.places) return Promise.resolve(window.google);
    if (googleMapsLoader) return googleMapsLoader;

    googleMapsLoader = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&region=ZA&v=weekly`;
        script.async = true;
        script.onload = () => resolve(window.google);
        script.onerror = () => reject(new Error('Address search could not be loaded.'));
        document.head.appendChild(script);
    });
    return googleMapsLoader;
}

function getAddressParts(place) {
    const parts = { streetNumber: '', streetName: '', suburb: '', postalCode: '' };
    (place.address_components || []).forEach(component => {
        const types = component.types || [];
        if (types.includes('street_number')) parts.streetNumber = component.long_name;
        if (types.includes('route')) parts.streetName = component.long_name;
        if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('locality')) parts.suburb = component.long_name;
        if (types.includes('postal_code')) parts.postalCode = component.long_name;
    });
    return parts;
}

function Checkout() {
    const { basket, total, clearBasket, removeFromBasket, removeSoldOutItems, updateItemQuantity } = useContext(BasketContext);
    const { businessStatus, refreshBusinessStatus } = useContext(BusinessStatusContext);
    const { isAuthenticated } = useContext(AuthContext);
    const [form, setForm] = useState(initialForm);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [addressSearchError, setAddressSearchError] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [basketNotice, setBasketNotice] = useState('');
    const addressSearchRef = useRef(null);
    const formRef = useRef(form);
    const history = useHistory();
    const deliveryFee = quote?.serviceable ? Number(quote.deliveryFee || 0) : 0;
    const grandTotal = total + deliveryFee;
    const isOpen = businessStatus?.canPlaceOrders ?? false;
    const items = useMemo(() => basket.map(line => ({ menuItemId: line.menuItemId, variantId: line.variantId, quantity: line.quantity })), [basket]);

    useEffect(() => {
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetch(buildApiUrl('/api/users/profile'), { headers: buildAuthHeaders() })
            .then(response => response.ok ? response.json() : Promise.reject(new Error()))
            .then(({ profile, addresses }) => {
                setSavedAddresses(addresses || []);
                if (profile?.fullName || profile?.phone) {
                    setForm(current => ({ ...current, name: current.name || profile.fullName || '', phone: current.phone || profile.phone || '' }));
                }
            })
            .catch(() => {});
    }, [isAuthenticated]);

    const applySavedAddress = address => {
        const nextValues = { ...formRef.current, ...address };
        setForm(nextValues);
        requestQuote(nextValues);
    };

    const requestQuote = async values => {
        setQuoteLoading(true);
        setAddressSearchError('');
        try {
            const response = await fetch(buildApiUrl('/api/orders/delivery-quote'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, foodTotal: total }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Unable to calculate delivery.');
            setQuote(data);
            if (!data.serviceable) setAddressSearchError(data.message);
        } catch (requestError) {
            setQuote(null);
            setAddressSearchError(requestError.message || 'Unable to calculate delivery.');
        } finally { setQuoteLoading(false); }
    };

    const reconcileBasketAvailability = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('/api/menu'));
            if (!response.ok) return;
            const removedItems = removeSoldOutItems(await response.json());
            if (removedItems.length) {
                setBasketNotice(`${removedItems.map(item => item.name).join(', ')} ${removedItems.length === 1 ? 'was' : 'were'} removed because ${removedItems.length === 1 ? 'it is' : 'they are'} sold out.`);
            }
        } catch (availabilityError) {
            console.error('Unable to refresh menu availability.', availabilityError);
        }
    }, [removeSoldOutItems]);

    useEffect(() => {
        reconcileBasketAvailability();
        const interval = setInterval(reconcileBasketAvailability, 30000);
        return () => clearInterval(interval);
    }, [reconcileBasketAvailability]);

    useEffect(() => {
        if (form.latitude && form.longitude) {
            requestQuote(form);
        }
    // The quote must follow food-total changes for special-area free delivery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);

    useEffect(() => {
        let active = true;
        loadGoogleMaps(GOOGLE_MAPS_API_KEY)
            .then(google => {
                if (!active || !addressSearchRef.current) return;
                const kznBounds = new google.maps.LatLngBounds(
                    new google.maps.LatLng(-30.9526, 29.0826),
                    new google.maps.LatLng(-27.3029, 32.9117)
                );
                const autocomplete = new google.maps.places.Autocomplete(addressSearchRef.current, {
                    types: ['address'],
                    componentRestrictions: { country: 'za' },
                    bounds: kznBounds,
                    strictBounds: true,
                    fields: ['address_components', 'formatted_address', 'geometry'],
                });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    const latitude = place.geometry?.location?.lat();
                    const longitude = place.geometry?.location?.lng();
                    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                        setQuote(null);
                        setAddressSearchError('Select a complete address from the suggested results.');
                        return;
                    }
                    const nextValues = {
                        ...formRef.current,
                        ...getAddressParts(place),
                        addressSearch: place.formatted_address || addressSearchRef.current.value,
                        latitude,
                        longitude,
                    };
                    setForm(nextValues);
                    requestQuote(nextValues);
                });
            })
            .catch(loadError => active && setAddressSearchError(loadError.message));
        return () => { active = false; };
    // Address search initializes once after the DOM input exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateForm = event => {
        const { name, value } = event.target;
        setForm(current => {
            const next = { ...current, [name]: value };
            if (name === 'addressSearch') {
                next.latitude = '';
                next.longitude = '';
                setQuote(null);
                setAddressSearchError('Choose an address from the search suggestions to calculate delivery.');
            }
            return next;
        });
    };

    const submitOrder = async event => {
        event.preventDefault();
        setError('');
        if (!basket.length) return setError('Your basket is empty.');
        if (!quote?.serviceable) return setError('Select a deliverable address from the address search results first.');

        const latestStatus = await refreshBusinessStatus();
        if (!latestStatus.canPlaceOrders) return setError(latestStatus.banner?.message || 'Ordering is currently unavailable.');

        setSubmitting(true);
        try {
            const response = await fetch(buildApiUrl('/api/orders'), {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ items, deliveryAddress: form, paymentMethod: 'Cash/Card on delivery' }),
            });
            const data = await response.json();
            if (!response.ok) {
                const requestError = new Error(data.error || 'Your order could not be placed.');
                requestError.code = data.code;
                throw requestError;
            }
            if (isAuthenticated) {
                await Promise.all([
                    fetch(buildApiUrl('/api/users/profile'), {
                        method: 'PUT', headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({ fullName: form.name, phone: form.phone }),
                    }),
                    fetch(buildApiUrl('/api/users/profile/addresses'), {
                        method: 'POST', headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({ ...form, label: 'Recent delivery address' }),
                    }),
                ]).catch(profileError => console.error('Order placed but profile details could not be saved.', profileError));
            } else if (data.customerAccessToken) {
                sessionStorage.setItem('pendingOrderAccount', JSON.stringify({
                    orderId: data.id,
                    customerAccessToken: data.customerAccessToken,
                    name: form.name,
                    phone: form.phone,
                }));
            }
            clearBasket();
            const token = data.customerAccessToken ? `?token=${encodeURIComponent(data.customerAccessToken)}` : '';
            history.push(`/order-confirmation/${data.id}${token}`);
        } catch (requestError) {
            if (requestError.code === 'SOLD_OUT') await reconcileBasketAvailability();
            setError(requestError.message || 'Your order could not be placed.');
        } finally { setSubmitting(false); }
    };

    if (!basket.length) return <main className="checkout-page">{basketNotice && <div className="checkout-notice" role="status">{basketNotice}</div>}<section className="empty-checkout"><p className="eyebrow">Your basket</p><h1>Nothing selected yet.</h1><p>Browse today's menu to add something delicious.</p><Link className="button button--primary" to="/">Browse menu</Link></section></main>;

    return <main className="checkout-page">
        <div className="checkout-heading"><p className="eyebrow">Secure order request</p><h1>Review and place your order.</h1><p>Select your address from the search results so we can confirm delivery distance and pricing.</p></div>
        {!isOpen && <div className="checkout-notice">{businessStatus?.banner?.message || 'Ordering is currently unavailable.'}</div>}
        {basketNotice && <div className="checkout-notice" role="status">{basketNotice}</div>}
        <div className="checkout-layout"><section className="checkout-form-panel"><form onSubmit={submitOrder}><fieldset><legend>Delivery details</legend>
            {isAuthenticated && savedAddresses.length > 0 && <div className="saved-addresses"><b>Saved addresses</b><div>{savedAddresses.map(address => <button type="button" key={address.id} onClick={() => applySavedAddress(address)}>{address.label}: {address.streetNumber} {address.streetName}, {address.suburb}</button>)}</div><small>Your contact details are prefilled. Edit them below when someone else is receiving.</small></div>}
            <div className="form-grid"><label>Full name<input name="name" value={form.name} onChange={updateForm} required autoComplete="name" /></label><label>Mobile number<input name="phone" value={form.phone} onChange={updateForm} required inputMode="tel" autoComplete="tel" /></label></div>
            <label className="address-search">Search delivery address <span><FaMapMarkerAlt /> Select your address from Google suggestions</span><input ref={addressSearchRef} name="addressSearch" value={form.addressSearch} onChange={updateForm} placeholder="Start typing your street address" required autoComplete="off" /></label>
            <div className="form-grid"><label>Street number<input name="streetNumber" value={form.streetNumber} onChange={updateForm} required /></label><label>Street / complex<input name="streetName" value={form.streetName} onChange={updateForm} required /></label></div><div className="form-grid"><label>Suburb<input name="suburb" value={form.suburb} onChange={updateForm} required /></label><label>Postal code<input name="postalCode" value={form.postalCode} onChange={updateForm} required inputMode="numeric" autoComplete="postal-code" /></label></div><label>Preferred time<select name="deliveryTime" value={form.deliveryTime} onChange={updateForm}><option value="ASAP">As soon as possible</option><option value="12:00 - 13:00">12:00 - 13:00</option><option value="13:00 - 14:00">13:00 - 14:00</option><option value="14:00 - 15:00">14:00 - 15:00</option><option value="15:00 - 16:00">15:00 - 16:00</option></select></label><label>Delivery instructions<textarea name="deliveryNote" value={form.deliveryNote} onChange={updateForm} rows="3" placeholder="Gate code, landmark, or other useful detail" /></label></fieldset><fieldset><legend>Delivery and payment</legend><p className="payment-copy"><FaCheckCircle /> Cash or card payment on delivery.</p>{quoteLoading && <p className="delivery-quote">Calculating delivery distance...</p>}{quote?.serviceable && <p className={`delivery-quote ${quote.isFreeDelivery ? 'delivery-quote--ok' : ''}`}><b>{quote.distanceKm} km by road</b> - {quote.message}</p>}{addressSearchError && <p className="form-error" role="alert">{addressSearchError}</p>}</fieldset>{error && <p className="form-error" role="alert">{error}</p>}<button className="button button--primary button--wide" type="submit" disabled={submitting || !isOpen || quoteLoading || !quote?.serviceable}>{submitting ? 'Placing order...' : `Place order - ${money(grandTotal)}`}</button></form></section><aside className="order-summary"><h2>Your order</h2>{basket.map(line => <div className="summary-line" key={line.lineId}><div><b>{line.name}</b><span>{line.variantName || 'Standard option'}</span><div className="quantity-control"><button type="button" onClick={() => updateItemQuantity(line.lineId, -1)} aria-label={`Remove one ${line.name}`}><FaMinus /></button><b>{line.quantity}</b><button type="button" onClick={() => updateItemQuantity(line.lineId, 1)} aria-label={`Add one ${line.name}`}><FaPlus /></button><button type="button" className="remove-line" onClick={() => removeFromBasket(line.lineId)} aria-label={`Remove ${line.name}`}><FaTrashAlt /></button></div></div><strong>{money(line.unitPrice * line.quantity)}</strong></div>)}<div className="summary-totals"><span>Food total <b>{money(total)}</b></span><span>Delivery <b>{quote?.serviceable ? deliveryFee ? money(deliveryFee) : 'Free' : 'Select address'}</b></span><strong>Total <b>{money(grandTotal)}</b></strong></div></aside></div>
    </main>;
}

export default Checkout;
